<template>
	<div class="row centered">
		<AppTableButtonGroup
			:buttons="buttons"
			:item="item"
			@picked-receipt-view="viewReceipt"
			@picked-receipt-upload="triggerUpload"
			@picked-receipt-delete="deleteReceipt" />
		<input
			ref="file-input"
			type="file"
			accept=".pdf,.jpg,.jpeg,.png"
			class="hidden-input"
			@change="onFileSelected" />
		<AppPopup
			ref="delete-popup"
			:title="getString('delete_receipt')"
			:body-text="getString('confirm_delete_receipt')"
			:buttons="deletePopupButtons" />
	</div>
</template>

<script>
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import API from "@/shared/helpers/API";
import {reduceFileSize} from "@/shared/helpers/uploadS3";
import {useUserStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore, useScriptStore} from "@/shared/stores";

const BUCKET_URL = "https://img.qromo.io/";
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];
const MAX_IMAGE_SIDE = 2048;
const COMPRESSION_TARGET_SIZE = 1024 * 1024;
const COMPRESSION_QUALITY = 0.7;

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const scriptStore = useScriptStore();
		return {userStore, scriptStore, getString: languageStore.getString, showNotification: notificationStore.showNotification};
	},
	name: "TransactionReceiptButton",
	components: {AppTableButtonGroup, AppPopup},
	props: {
		item: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			loading: false
		};
	},
	computed: {
		deletePopupButtons() {
			return [
				{
					text: this.getString('cancel'),
					negative: true,
					callback: () => this.$refs["delete-popup"].toggleStatus(false)
				},
				{
					text: this.getString('delete'),
					callback: this.confirmDelete
				}
			];
		},
		buttons() {
			if (this.item.receipt_file) {
				return [
					{
						iconName: "icon-eye-blue",
						emitName: "picked-receipt-view",
						title: this.getString('view')
					},
					{
						iconName: "icon-trash-blue",
						emitName: "picked-receipt-delete",
						loading: this.loading,
						title: this.getString('delete_receipt')
					}
				];
			}
			return [
				{
					iconName: "icon-upload-document",
					emitName: "picked-receipt-upload",
					loading: this.loading,
					title: this.getString('upload_receipt')
				}
			];
		}
	},
	methods: {
		viewReceipt() {
			if (!this.item.receipt_file) { return; }
			window.open(BUCKET_URL + this.item.receipt_file, "_blank");
		},
		triggerUpload() {
			if (this.loading) { return; }
			this.$refs["file-input"].click();
		},
		onFileSelected(event) {
			const file = event.target.files && event.target.files[0];
			// Reset the input so selecting the same file again re-triggers change
			event.target.value = "";
			if (!file) { return; }

			const extension = file.name.split(".").pop().toLowerCase();
			if (!ALLOWED_EXTENSIONS.includes(extension)) {
				this.showNotification("generic-error");
				return;
			}

			this.loading = true;

			if (extension === "pdf") {
				this.uploadReceipt(file, file.name);
				return;
			}

			this.scriptStore.loadScript("imageCompressor", () => {
				reduceFileSize(file, MAX_IMAGE_SIDE, MAX_IMAGE_SIDE, COMPRESSION_TARGET_SIZE, COMPRESSION_QUALITY, blob => {
					this.uploadReceipt(blob, this.compressedFileName(file, blob));
				});
			});
		},
		compressedFileName(file, blob) {
			const baseName = file.name.replace(/\.[^.]+$/, "");
			return baseName + (blob.type === "image/png" ? ".png" : ".jpg");
		},
		uploadReceipt(file, fileName) {
			// Size is validated server-side when signing the URL
			API.init().getCardReceiptUploadUrl(this.userStore.currBusinessId, fileName, file.size, (r) => {
				if (r && r.result === "OK" && r.url && r.file_key) {
					fetch(r.url, {
						method: "PUT",
						headers: {"Content-Type": file.type},
						body: file
					})
						.then(response => {
							if (!response.ok) { throw new Error("S3 refused the upload: " + response.status); }
							this.saveReceiptReference(r.file_key);
						})
						.catch(err => {
							console.warn("ERROR uploading receipt: ", err);
							this.loading = false;
							this.showNotification("generic-error");
						});
				} else {
					this.loading = false;
					if (r && r.details === "file_too_big") {
						this.showNotification("upload-file-too-big");
					} else {
						this.showNotification("generic-error");
					}
				}
			});
		},
		deleteReceipt() {
			if (this.loading) { return; }
			this.$refs["delete-popup"].toggleStatus(true);
		},
		confirmDelete() {
			this.$refs["delete-popup"].toggleStatus(false);
			this.loading = true;
			// An empty key clears the receipt server-side
			this.saveReceiptReference("");
		},
		saveReceiptReference(fileKey) {
			API.init().saveCardTransactionReceipt(
				this.userStore.currBusinessId,
				this.item.type,
				this.item.id,
				fileKey,
				(r) => {
					this.loading = false;
					if (r && r.result === "OK") {
						this.showNotification("generic-success");
						this.item.receipt_file = fileKey === "" ? null : fileKey;
					} else {
						this.showNotification("generic-error");
					}
				}
			);
		}
	}
}
</script>

<style scoped lang="scss">
.hidden-input {
	display: none;
}
</style>
